import React, { useState, useCallback } from 'react';
import Button from '../components/Button';
import TextString from '../components/Text';
import MuiDialog from '@material-ui/core/Dialog';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import styled from 'styled-components/macro';
import MuiIconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Animation from '../components/Animation';
import useTranslations from '../hooks/useTranslations';
import { Input, Tooltip } from '@material-ui/core';
import { RelativeLoader } from '../components/Loader';
import useGlobalVariables from '../hooks/useGlobalVariables';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Colors from './Colors';

const Dialog = styled(MuiDialog)`
  && {
    ${({ $isMobileView }) => !$isMobileView && 'min-width: 600px;'}
    [class*='MuiDialog-paper'] {
      ${({ overflow }) =>
        overflow ? 'overflow: ' + overflow + ' !important;' : ''}
    }

    ${({ $customCSS }) => $customCSS}
  }
`;

const DialogTitle = styled(MuiDialogTitle)`
  && {
    padding: 2rem 2rem 1rem;
    h2 {
      margin: 0;
    }
  }

  ${({ $customCSS }) => $customCSS}
`;

const DialogContent = styled(MuiDialogContent)`
  && {
    padding-top: 0px;
    padding: ${({ $extraTopPadding }) => ($extraTopPadding ? '2.5rem' : '1rem')}
      2rem 2rem 2rem;
    ${({ overflow }) =>
      overflow ? 'overflow: ' + overflow + ' !important;' : ''}
  }

  h2 {
    ${({ centeredtTitle }) => centeredtTitle && 'text-align: center;'}
  }
`;

const IconButton = styled(MuiIconButton)`
  z-index: 10;
  transition: 0.5s;

  &:hover {
    color: #4c84ff;
  }
`;

const DialogActions = styled(MuiDialogActions)`
  && {
    flex-direction: column;
    align-items: flex-end;

    animation-name: ${({ displayConfirm }) =>
      displayConfirm ? 'swapColors' : ''};
    animation-duration: 1.25s;
  }

  @keyframes swapColors {
    0% {
      background-color: #f7f8fc;
    }
    25% {
      background-color: #ffd576;
    }
    50% {
      background-color: #f7f8fc;
    }
    75% {
      background-color: #ffd576;
    }
    100% {
      background-color: #f7f8fc;
    }
  }
`;

const RelativeLoaderStyles = `
  display: flex;
  flex-direction: column;
`;

const XBtnContainer = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
`;

const DeleteValError = styled.div`
  color: ${Colors.OT_RED};
  margin-top: 0.5rem;
  position: absolute;
`;

const TypeDeleteForm = ({
  onBlur = () => {},
  onChange = () => {},
  showDeleteValError = false,
  value = ''
}) => {
  const t = useTranslations();
  return (
    <>
      <div>{t('delete_form_dialog.type-delete')}</div>
      <Input
        value={value}
        onBlur={onBlur}
        onChange={onChange}
        inputProps={{ 'data-testid': 'type-delete-confirm-field' }}
      />
      {showDeleteValError ? (
        <DeleteValError>
          {t('delete_form_dialog.type-delete-error')}
        </DeleteValError>
      ) : (
        ''
      )}
    </>
  );
};

/**
 *
 * @param {boolean} open - opne/close state
 * @param {string} title - title of the dialog (can be id of text from the .dat dictionary)
 * @param {string} content - content of the dialog (can be id of text from the .dat dictionary)
 * @param {function} handleClose - function to handle the closure of the dialog (cancel or X button clicked)
 * @param {function} handleConfirm - function to handle the confirmation of the dialog (confirm button clicked)
 * @param {objects array} buttons - the button to have in the dialog. by default there are 2 buttons (confirm and cancel), they can be overwritten by different set of buttons. if you want to define your own buttons just follow few ground rules - dismiss buttons should have variant='outlined', confirmation buttons must have the onClick attribute with 'confirm' string in it, this is so it will know to reffer to this button as a confirmation button
 *
 */

function GeneralDialog({
  open,
  title,
  content,
  handleClose: actualHandleClose,
  handleConfirm: actualHandleConfirm,
  confirmCancel = true,
  disableBackdropClick,
  isDirty = false,
  buttons,
  actionAlert,
  children,
  centeredtTitle,
  overflow,
  variant = 'info',
  noX,
  isLoading = false,
  onCancelConfirmationMessage = 'dialog.cancelOperation.text',
  onConfirmConfirmationMessage = '',
  confirmConfirmation,
  customCSS,
  titleBlockProps = {},
  confirmType,
  ...muiDialogProps
}) {
  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);
  const [displayConfirmConfirmation, setDisplayConfirmConfirmation] =
    useState(false);
  const [typeDeleteVal, setTypeDeleteVal] = useState('');
  const [showDeleteValError, setShowDeleteValError] = useState(false);
  const t = useTranslations();
  const { isMobileView } = useGlobalVariables();

  const handleClose = () => {
    setShowDeleteValError(false);
    setTypeDeleteVal('');
    isDirty && confirmCancel
      ? setDisplayCancelConfirmation(true)
      : actualHandleClose();
  };

  const handleConfirm = () => {
    setShowDeleteValError(false);
    setTypeDeleteVal('');
    confirmConfirmation
      ? setDisplayConfirmConfirmation(true)
      : actualHandleConfirm();
  };

  const getButtons = useCallback(
    (variant, handleConfirm, handleClose) => {
      switch (variant) {
        case 'feature':
          return [{ text: 'dialog.feature.confirm', onClick: handleConfirm }];
        case 'delete':
          return [
            {
              text: 'dialog.cancel',
              variant: 'outlined',
              onClick: handleClose
            },
            {
              text: 'dialog.delete',
              color: 'secondary',
              onClick: handleConfirm
            }
          ];
        case 'confirm':
          return [
            {
              text: 'dialog.no',
              onClick: handleClose
            },
            { text: 'dialog.yes', onClick: handleConfirm, variant: 'outlined' }
          ];
        case 'info':
        default:
          return [
            {
              text: 'dialog.cancel',
              variant: 'outlined',
              onClick: handleClose
            },
            { text: 'dialog.continue', onClick: handleConfirm }
          ];
      }
    },
    [displayCancelConfirmation, variant, handleConfirm]
  );

  const onTypeDeleteChange = useCallback(e => {
    const newValue = e.target.value;
    setTypeDeleteVal(newValue);
    if (newValue === 'DELETE') {
      setShowDeleteValError(false);
    }
  }, []);

  const onTypeDeleteBlur = useCallback(
    e => {
      if (typeDeleteVal !== 'DELETE') {
        setShowDeleteValError(true);
      } else {
        setShowDeleteValError(false);
      }
    },
    [typeDeleteVal]
  );

  return (
    <Dialog
      open={open}
      overflow={overflow}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      disableBackdropClick={disableBackdropClick || !actualHandleClose}
      $isMobileView={isMobileView}
      fullScreen={isMobileView}
      $customCSS={customCSS}
      {...muiDialogProps}
    >
      <RelativeLoader isLoading={isLoading} customCSS={RelativeLoaderStyles}>
        {actualHandleClose && !noX && (
          <XBtnContainer>
            <Tooltip title="Close dialog" arrow>
              <IconButton aria-label="close" onClick={handleClose} size="small">
                <CloseIcon id="close-dialog" />
              </IconButton>
            </Tooltip>
          </XBtnContainer>
        )}
        {title && title.length && (
          <DialogTitle {...titleBlockProps}>
            {t(title)}
            {/* <TextString content={title} variant="h2" id="alert-dialog-title" /> */}
          </DialogTitle>
        )}
        <DialogContent
          $extraTopPadding={!!!title || !!!title.length}
          centeredtTitle={centeredtTitle}
          overflow={overflow}
        >
          {children ? children : <TextString content={content} />}
          {confirmType === 'typeDelete' ? (
            <TypeDeleteForm
              value={typeDeleteVal}
              onBlur={onTypeDeleteBlur}
              onChange={onTypeDeleteChange}
              showDeleteValError={showDeleteValError}
            />
          ) : (
            ''
          )}
        </DialogContent>
        <DialogActions
          displayConfirm={displayCancelConfirmation}
          className="action-buttons-wrapper"
        >
          <Animation
            type="collapse"
            appear={false}
            in={!displayCancelConfirmation && !displayConfirmConfirmation}
          >
            <div>
              {actionAlert ? (
                <small>
                  <i>{t(actionAlert)}&nbsp;</i>
                </small>
              ) : (
                ''
              )}
              <Buttons
                {...{
                  getButtons,
                  buttons,
                  variant,
                  handleConfirm,
                  actualHandleClose,
                  handleClose,
                  confirmType,
                  typeDeleteVal
                }}
              />
            </div>
          </Animation>
          <Animation type="collapse" in={displayConfirmConfirmation}>
            <div>
              {t(onConfirmConfirmationMessage) + ' '}
              <Buttons
                {...{ getButtons }}
                handleConfirm={() => {
                  actualHandleConfirm();
                  setDisplayConfirmConfirmation(false);
                }}
                handleClose={() => setDisplayConfirmConfirmation(false)}
                variant="confirm"
              />
            </div>
          </Animation>
          <Animation type="collapse" in={displayCancelConfirmation}>
            <div>
              {t(onCancelConfirmationMessage) + ' '}
              <Buttons
                {...{ getButtons }}
                handleConfirm={() => {
                  actualHandleClose();
                  setDisplayCancelConfirmation(false);
                }}
                handleClose={() => setDisplayCancelConfirmation(false)}
                variant="confirm"
              />
            </div>
          </Animation>
        </DialogActions>
      </RelativeLoader>
    </Dialog>
  );
}

const Buttons = ({
  confirmType,
  getButtons,
  buttons,
  typeDeleteVal,
  variant,
  handleConfirm,
  handleClose
}) => {
  buttons = !buttons
    ? getButtons(variant, handleConfirm, handleClose)
    : buttons;
  return buttons.map((button, i) => (
    <Button
      key={`general-dialog-btn-${i}`}
      onClick={
        button.onClick === 'confirm'
          ? handleConfirm
          : button.onClick
          ? button.onClick
          : handleClose
      }
      color={button.color ? button.color : 'primary'}
      variant={button.variant ? button.variant : 'contained'}
      data-testid={button['data-testid'] || `${button.text}-dialog-button`}
      disabled={
        button.disabled ||
        (button.onClick === 'confirm' &&
          confirmType === 'typeDelete' &&
          typeDeleteVal !== 'DELETE') ||
        false
      }
    >
      <TextString content={button.text} />
    </Button>
  ));
};

export default GeneralDialog;
